import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { formatCOP, formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { Database, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ADMIN } from '@/constants/testIds';

export default function AdminSettings() {
  const [s, setS] = useState({
    activation_fee: '', commission_pct: '',
    basic_plan_price: '', basic_plan_commission_pct: '',
    none_plan_product_limit: '',
    basic_plan_product_limit: '',
    pro_plan_price: '', pro_plan_commission_pct: '',
    pro_plan_product_limit: '',
    sponsorship_tiers: [
      { days: 7, price: 50000 },
      { days: 15, price: 70000 },
      { days: 30, price: 100000 },
    ],
    // Iter 45 — Vigencia y renovación (calendario)
    activation_period_months: '',
    activation_expiry_notice_days: '',
    basic_plan_period_months: '', pro_plan_period_months: '',
    plan_expiry_notice_days: '',
    none_plan_price: '', none_plan_period_months: '',
    launch_benefit_limit: '', launch_benefit_days: '', launch_benefit_commission_pct: '',
    // Company info (FASE C+ centralized contact)
    company_legal_name: '', company_nit: '', company_address: '',
    company_phone: '', company_email: '',
    social_instagram: '', social_tiktok: '', social_facebook: '',
  });
  const [loading, setLoading] = useState(false);
  const [backups, setBackups] = useState({ items: [], disk_usage_mb: 0, free_disk_mb: 0 });
  const [backupBusy, setBackupBusy] = useState(false);

  useSEO({
    title: 'Configuración de la plataforma | Administración',
    description: 'Ajusta comisiones, precios de planes y gestiona los backups automáticos.',
    canonical: '/admin/configuracion',
  });

  const loadBackups = () => {
    api.get('/admin/backups')
      .then(({ data }) => setBackups(data))
      .catch(() => {});
  };

  useEffect(() => {
    api.get('/admin/settings').then(({ data }) => setS({
      activation_fee: data.activation_fee,
      commission_pct: data.commission_pct,
      basic_plan_price: data.basic_plan_price,
      basic_plan_commission_pct: data.basic_plan_commission_pct,
      none_plan_product_limit: data.none_plan_product_limit ?? '',
      basic_plan_product_limit: data.basic_plan_product_limit ?? '',
      pro_plan_price: data.pro_plan_price,
      pro_plan_commission_pct: data.pro_plan_commission_pct,
      pro_plan_product_limit: data.pro_plan_product_limit ?? '',
      sponsorship_tiers: (data.sponsorship_tiers && data.sponsorship_tiers.length > 0)
        ? data.sponsorship_tiers
        : [
            { days: 7, price: 50000 },
            { days: 15, price: 70000 },
            { days: 30, price: 100000 },
          ],
      activation_period_months: data.activation_period_months ?? '',
      activation_expiry_notice_days: data.activation_expiry_notice_days ?? '',
      basic_plan_period_months: data.basic_plan_period_months ?? '',
      pro_plan_period_months: data.pro_plan_period_months ?? '',
      plan_expiry_notice_days: data.plan_expiry_notice_days ?? '',
      none_plan_price: data.none_plan_price ?? '',
      none_plan_period_months: data.none_plan_period_months ?? '',
      launch_benefit_limit: data.launch_benefit_limit ?? '',
      launch_benefit_days: data.launch_benefit_days ?? '',
      launch_benefit_commission_pct: data.launch_benefit_commission_pct ?? '',
      company_legal_name: data.company_legal_name || '',
      company_nit: data.company_nit || '',
      company_address: data.company_address || '',
      company_phone: data.company_phone || '',
      company_email: data.company_email || '',
      social_instagram: data.social_instagram || '',
      social_tiktok: data.social_tiktok || '',
      social_facebook: data.social_facebook || '',
    }));
    loadBackups();
  }, []);

  const runBackup = async () => {
    setBackupBusy(true);
    try {
      await api.post('/admin/backups/run');
      toast.success('Backup creado correctamente');
      loadBackups();
    } catch {
      toast.error('No se pudo crear el backup');
    } finally { setBackupBusy(false); }
  };

  const downloadBackup = (name) => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    const token = localStorage.getItem('session_token') || '';
    fetch(`${base}/api/admin/backups/${name}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    })
      .then(r => r.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = name; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Error al descargar'));
  };

  const save = async () => {
    setLoading(true);
    try {
      await api.put('/admin/settings', {
        commission_pct: parseFloat(s.commission_pct),
        basic_plan_price: parseFloat(s.basic_plan_price),
        basic_plan_commission_pct: parseFloat(s.basic_plan_commission_pct),
        none_plan_product_limit: s.none_plan_product_limit === '' ? undefined : parseInt(s.none_plan_product_limit, 10),
        basic_plan_product_limit: s.basic_plan_product_limit === '' ? undefined : parseInt(s.basic_plan_product_limit, 10),
        pro_plan_price: parseFloat(s.pro_plan_price),
        pro_plan_commission_pct: parseFloat(s.pro_plan_commission_pct),
        pro_plan_product_limit: s.pro_plan_product_limit === '' ? undefined : parseInt(s.pro_plan_product_limit, 10),
        sponsorship_tiers: (s.sponsorship_tiers || []).map((t) => ({
          days: parseInt(t.days, 10) || 0,
          price: parseFloat(t.price) || 0,
        })).filter((t) => t.days > 0),
        activation_period_months: s.activation_period_months === '' ? undefined : parseInt(s.activation_period_months, 10),
        activation_expiry_notice_days: s.activation_expiry_notice_days === '' ? undefined : parseInt(s.activation_expiry_notice_days, 10),
        basic_plan_period_months: s.basic_plan_period_months === '' ? undefined : parseInt(s.basic_plan_period_months, 10),
        pro_plan_period_months: s.pro_plan_period_months === '' ? undefined : parseInt(s.pro_plan_period_months, 10),
        plan_expiry_notice_days: s.plan_expiry_notice_days === '' ? undefined : parseInt(s.plan_expiry_notice_days, 10),
        none_plan_price: s.none_plan_price === '' ? undefined : parseFloat(s.none_plan_price),
        none_plan_period_months: s.none_plan_period_months === '' ? undefined : parseInt(s.none_plan_period_months, 10),
        launch_benefit_limit: s.launch_benefit_limit === '' ? undefined : parseInt(s.launch_benefit_limit, 10),
        launch_benefit_days: s.launch_benefit_days === '' ? undefined : parseInt(s.launch_benefit_days, 10),
        launch_benefit_commission_pct: s.launch_benefit_commission_pct === '' ? undefined : parseFloat(s.launch_benefit_commission_pct),
        company_legal_name: s.company_legal_name,
        company_nit: s.company_nit,
        company_address: s.company_address,
        company_phone: s.company_phone,
        company_email: s.company_email,
        social_instagram: s.social_instagram,
        social_tiktok: s.social_tiktok,
        social_facebook: s.social_facebook,
      });
      toast.success('Configuración guardada');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error al guardar');
    } finally { setLoading(false); }
  };

  const setField = (k) => (e) => setS({ ...s, [k]: e.target.value });

  return (
    <Layout>
      {/* Botón "Guardar" sticky en la parte inferior — visible siempre para
          que el admin pueda persistir cambios de cualquier sección sin
          scrollear hasta abajo. */}
      <div className="pb-24">
      <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
        <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">Configuración</h1>
        <Link to="/admin/configuracion/envios" className="mt-3 inline-block text-sm font-semibold text-orange-700 hover:underline"
          data-testid="admin-settings-logistics-link">Envíos y logística →</Link>

        {/* 1. Sin plan */}
        <Section
          title="Modalidad Sin plan"
          subtitle="Modalidad base de pago. Reemplaza la antigua activación: tiene precio, vigencia, comisión y límite configurables."
          accent="orange"
        >
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Field label="Precio Sin plan (COP)" testid="admin-none-plan-price-input"
                value={s.none_plan_price} onChange={setField('none_plan_price')} type="number" min="0" />
              <p className="text-xs text-slate-500 mt-1">{formatCOP(s.none_plan_price)}</p>
            </div>
            <div>
              <Field label="Vigencia Sin plan (meses)" testid="admin-none-plan-period-months"
                value={s.none_plan_period_months} onChange={setField('none_plan_period_months')} type="number" min="1" />
              <p className="text-xs text-slate-500 mt-1">Meses calendario · al vencer el vendedor queda bloqueado</p>
            </div>
            <div>
              <Field label="Comisión por venta (%)" testid={ADMIN.commissionInput}
                value={s.commission_pct} onChange={setField('commission_pct')} type="number" step="0.1" />
            </div>
            <div>
              <Field label="Límite de productos" testid="admin-none-product-limit-input"
                value={s.none_plan_product_limit} onChange={setField('none_plan_product_limit')} type="number" min="0" />
              <p className="text-xs text-slate-500 mt-1">0 = ilimitado</p>
            </div>
          </div>
        </Section>

        {/* 2. Beneficio de lanzamiento */}
        <Section
          title="Beneficio de lanzamiento"
          subtitle="Para los primeros vendedores aprobados desde esta versión: modalidad sin costo y comisión especial durante los días indicados. Una sola vez por vendedor."
        >
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Field label="Cupos" testid="admin-launch-benefit-limit-input"
                value={s.launch_benefit_limit} onChange={setField("launch_benefit_limit")} type="number" min="1" />
            </div>
            <div>
              <Field label="Duración (días)" testid="admin-launch-benefit-days-input"
                value={s.launch_benefit_days} onChange={setField('launch_benefit_days')} type="number" min="1" />
            </div>
            <div>
              <Field label="Comisión (%)" testid="admin-launch-benefit-commission-input"
                value={s.launch_benefit_commission_pct} onChange={setField('launch_benefit_commission_pct')} type="number" step="0.1" min="0" />
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">Los cambios aplican a nuevas asignaciones; los beneficios ya otorgados conservan sus fechas y comisión.</p>
        </Section>

        {/* 3. Plan Básico */}
        <Section title="Plan Básico" subtitle="Modalidad con comisión reducida vs Sin plan.">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Field label="Comisión Básico (%)" testid="admin-basic-commission-input"
                value={s.basic_plan_commission_pct} onChange={setField('basic_plan_commission_pct')} type="number" step="0.1" />
            </div>
            <div>
              <Field label="Precio Básico (COP)" testid={ADMIN.subscriptionInput}
                value={s.basic_plan_price} onChange={setField('basic_plan_price')} type="number" />
              <p className="text-xs text-slate-500 mt-1">{formatCOP(s.basic_plan_price)}</p>
            </div>
            <div>
              <Field label="Límite de productos" testid="admin-basic-product-limit-input"
                value={s.basic_plan_product_limit} onChange={setField('basic_plan_product_limit')} type="number" min="0" />
              <p className="text-xs text-slate-500 mt-1">0 = ilimitado</p>
            </div>
            <div>
              <Field label="Vigencia Básico (meses)" testid="admin-basic-plan-period-months"
                value={s.basic_plan_period_months} onChange={setField('basic_plan_period_months')} type="number" min="1" />
              <p className="text-xs text-slate-500 mt-1">Meses calendario · fecha de vencimiento de Básico</p>
            </div>
          </div>
        </Section>

        {/* 4. Plan PRO */}
        <Section title="Plan PRO" subtitle="Modalidad premium: comisión mínima + destaque en catálogo + badge PRO." accent="orange-strong">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Field label="Comisión PRO (%)" testid="admin-pro-commission-input"
                value={s.pro_plan_commission_pct} onChange={setField('pro_plan_commission_pct')} type="number" step="0.1" />
            </div>
            <div>
              <Field label="Precio PRO (COP)" testid="admin-pro-subscription-input"
                value={s.pro_plan_price} onChange={setField('pro_plan_price')} type="number" />
              <p className="text-xs text-slate-500 mt-1">{formatCOP(s.pro_plan_price)}</p>
            </div>
            <div>
              <Field label="Límite de productos" testid="admin-pro-product-limit-input"
                value={s.pro_plan_product_limit} onChange={setField('pro_plan_product_limit')} type="number" min="0" />
              <p className="text-xs text-slate-500 mt-1">0 = ilimitado</p>
            </div>
            <div>
              <Field label="Vigencia PRO (meses)" testid="admin-pro-plan-period-months"
                value={s.pro_plan_period_months} onChange={setField('pro_plan_period_months')} type="number" min="1" />
              <p className="text-xs text-slate-500 mt-1">Meses calendario · fecha de vencimiento de PRO</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4 border-t border-slate-200 pt-4">
            <div>
              <Field label="Aviso vencimiento plan (días)" testid="admin-plan-notice-days"
                value={s.plan_expiry_notice_days} onChange={setField('plan_expiry_notice_days')} type="number" min="1" />
              <p className="text-xs text-slate-500 mt-1">Notificación in-app + email + SMS · default 5</p>
            </div>
          </div>
        </Section>

        {/* Tarifario de PROMOCIÓN pagada por vendedor */}
        <Section
          title="Tarifario de promoción (sponsorship)"
          subtitle="Precios que cobra la plataforma al vendedor para posicionar un producto como destacado. Los cambios se reflejan de inmediato en el flujo de compra del vendedor."
          accent="orange"
        >
          <div className="space-y-3">
            {(s.sponsorship_tiers || []).map((tier, idx) => (
              <div key={idx} className="grid grid-cols-[1fr,2fr,auto] gap-3 items-end">
                <div>
                  <Field
                    label="Días"
                    testid={`admin-sponsor-tier-${idx}-days`}
                    type="number" min="1"
                    value={tier.days}
                    onChange={(e) => {
                      const next = [...s.sponsorship_tiers];
                      next[idx] = { ...next[idx], days: e.target.value };
                      setS({ ...s, sponsorship_tiers: next });
                    }}
                  />
                </div>
                <div>
                  <Field
                    label="Precio (COP)"
                    testid={`admin-sponsor-tier-${idx}-price`}
                    type="number" min="0"
                    value={tier.price}
                    onChange={(e) => {
                      const next = [...s.sponsorship_tiers];
                      next[idx] = { ...next[idx], price: e.target.value };
                      setS({ ...s, sponsorship_tiers: next });
                    }}
                  />
                  <p className="text-xs text-slate-500 mt-1">{formatCOP(tier.price)}</p>
                </div>
                <Button
                  variant="outline"
                  className="rounded-none h-10 border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => setS({
                    ...s,
                    sponsorship_tiers: s.sponsorship_tiers.filter((_, i) => i !== idx),
                  })}
                  data-testid={`admin-sponsor-tier-${idx}-remove`}
                >
                  Quitar
                </Button>
              </div>
            ))}
            <Button
              variant="outline"
              className="rounded-none w-full h-10 border-dashed"
              data-testid="admin-sponsor-tier-add"
              onClick={() => setS({
                ...s,
                sponsorship_tiers: [...(s.sponsorship_tiers || []), { days: 0, price: 0 }],
              })}
            >
              + Agregar duración
            </Button>
          </div>
        </Section>

        {/* Datos de la empresa — canonical contact info */}
        <Section title="Datos de la empresa" subtitle="Información de contacto oficial. Se muestra en el footer, /contacto y correos. Modifícala aquí sin necesidad de tocar código." accent="orange-strong">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Razón social" testid="admin-company-legal-name"
              value={s.company_legal_name} onChange={setField('company_legal_name')} />
            <Field label="NIT" testid="admin-company-nit"
              value={s.company_nit} onChange={setField('company_nit')} />
            <div className="sm:col-span-2">
              <Field label="Dirección" testid="admin-company-address"
                value={s.company_address} onChange={setField('company_address')} />
            </div>
            <Field label="Celular / WhatsApp (solo dígitos)" testid="admin-company-phone"
              value={s.company_phone} onChange={setField('company_phone')} />
            <Field label="Correo" testid="admin-company-email" type="email"
              value={s.company_email} onChange={setField('company_email')} />
            <Field label="URL Instagram" testid="admin-social-instagram"
              value={s.social_instagram} onChange={setField('social_instagram')} placeholder="https://www.instagram.com/..." />
            <Field label="URL TikTok" testid="admin-social-tiktok"
              value={s.social_tiktok} onChange={setField('social_tiktok')} placeholder="https://www.tiktok.com/@..." />
            <div className="sm:col-span-2">
              <Field label="URL Facebook (vacío = oculto en footer)" testid="admin-social-facebook"
                value={s.social_facebook} onChange={setField('social_facebook')} placeholder="Pendiente…" />
            </div>
          </div>
        </Section>

        <Button
          data-testid={ADMIN.saveSettingsBtn}
          onClick={save} disabled={loading}
          className="mt-6 w-full rounded-none bg-slate-900 hover:bg-slate-800 h-11 font-bold"
        >
          {loading ? 'Guardando...' : 'Guardar configuración'}
        </Button>

        {/* Backups */}
        <Section
          title="Backups automáticos"
          subtitle="Respaldo diario de toda la base de datos a las 03:15 UTC. Conserva los últimos 14 backups."
        >
          <div className="flex items-center justify-between gap-4 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <Database className="h-4 w-4 text-slate-500" />
              <span>{backups.items.length} backups · {backups.disk_usage_mb} MB usados · {backups.free_disk_mb} MB libres</span>
            </div>
            <Button
              data-testid="admin-run-backup-btn"
              onClick={runBackup}
              disabled={backupBusy}
              variant="outline"
              className="rounded-none h-9 text-xs"
            >
              <RefreshCw className={`mr-2 h-3.5 w-3.5 ${backupBusy ? 'animate-spin' : ''}`} />
              {backupBusy ? 'Creando...' : 'Crear backup ahora'}
            </Button>
          </div>
          {backups.items.length > 0 && (
            <div className="mt-4 border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-auto">
              {backups.items.map((b) => (
                <div key={b.name} className="flex items-center justify-between px-3 py-2 text-xs hover:bg-slate-50">
                  <div>
                    <div className="font-mono text-slate-900">{b.name}</div>
                    <div className="text-slate-500">
                      {formatDate(b.created_at)} · {(b.size_bytes / 1024).toFixed(1)} KB
                    </div>
                  </div>
                  <button
                    onClick={() => downloadBackup(b.name)}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 hover:border-slate-900"
                  >
                    <Download className="h-3 w-3" /> Descargar
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
      </div>
      {/* Sticky save bar — siempre accesible mientras hay cambios pendientes. */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur shadow-lg">
        <div className="mx-auto max-w-3xl px-4 py-3 sm:px-6 flex items-center justify-between gap-3">
          <div className="text-xs text-slate-500 hidden sm:block">
            Los cambios se aplican inmediatamente al guardar.
          </div>
          <Button
            data-testid="admin-save-sticky"
            onClick={save} disabled={loading}
            className="rounded-none bg-orange-500 hover:bg-orange-600 text-slate-900 h-11 px-6 font-bold"
          >
            {loading ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </div>
    </Layout>
  );
}

const Section = ({ title, subtitle, accent, children }) => {
  const cls = accent === 'orange-strong' ? 'border-2 border-orange-500 bg-orange-50'
    : accent === 'orange' ? 'border-2 border-orange-400 bg-orange-50/60'
    : 'border border-slate-200 bg-white';
  return (
    <div className={`mt-6 p-6 ${cls}`}>
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Sección</div>
      <h2 className="font-display text-xl font-bold text-slate-900 mt-0.5">{title}</h2>
      {subtitle && <p className="text-xs text-slate-600 mt-1">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
};

const Field = ({ label, value, onChange, testid, type = 'text', step, placeholder, min }) => (
  <>
    <label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{label}</label>
    <Input data-testid={testid} type={type} step={step} min={min} value={value} onChange={onChange} placeholder={placeholder} className="rounded-none mt-1" />
  </>
);
